<template>
  <div class="photo-input" :class="{ 'photo-input--disabled': disabled }">
    <div class="photo-input__head">
      <v-icon small color="#2d8659" class="mr-1">mdi-camera-outline</v-icon>
      <span class="photo-input__label">{{ label }}</span>
      <span v-if="max > 1" class="photo-input__count">{{ items.length }} / {{ max }}</span>
    </div>

    <div v-if="items.length || pending.length" class="photo-input__grid">
      <div v-for="(item, i) in items" :key="'f' + item.id" class="photo-input__tile">
        <button type="button" class="photo-input__open" :aria-label="`Deschide fotografia ${i + 1}`" @click="openViewer(i)">
          <auth-image :file-id="item.id" :lazy="false" />
        </button>
        <v-btn
          v-if="!disabled"
          class="photo-input__remove"
          fab
          x-small
          depressed
          color="white"
          :aria-label="`Șterge fotografia ${i + 1}`"
          @click="remove(item)"
        >
          <v-icon small color="#b3362f">mdi-close</v-icon>
        </v-btn>
      </div>

      <div v-for="p in pending" :key="'p' + p.key" class="photo-input__tile photo-input__tile--busy">
        <img v-if="p.previewUrl" :src="p.previewUrl" alt="" />
        <div class="photo-input__progress">
          <v-progress-circular
            :indeterminate="p.stage === 'prepare' || p.progress >= 100"
            :value="p.progress"
            size="40"
            width="4"
            color="white"
          >
            <span v-if="p.stage === 'upload' && p.progress < 100" class="photo-input__percent">{{ p.progress }}</span>
          </v-progress-circular>
          <span class="photo-input__stage">{{ p.stage === "prepare" ? "Se pregătește…" : "Se încarcă…" }}</span>
        </div>
        <v-btn class="photo-input__remove" fab x-small depressed color="white" aria-label="Anulează încărcarea" @click="cancel(p)">
          <v-icon small color="#b3362f">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!disabled && canPick" class="photo-input__actions">
      <v-btn v-if="hasCamera" outlined color="#2d8659" class="photo-input__btn" :disabled="busy && max === 1" @click="pick('camera')">
        <v-icon left>mdi-camera</v-icon>
        {{ replacing ? "Fotografiază din nou" : "Fotografiază" }}
      </v-btn>
      <v-btn outlined color="#2d8659" class="photo-input__btn" :disabled="busy && max === 1" @click="pick('gallery')">
        <v-icon left>mdi-image-multiple-outline</v-icon>
        {{ galleryLabel }}
      </v-btn>
    </div>
    <div v-if="hint && !disabled" class="photo-input__hint">{{ hint }}</div>

    <input ref="camera" type="file" accept="image/*" capture="environment" class="photo-input__file" @change="onPicked" />
    <input ref="gallery" type="file" accept="image/*" :multiple="max > 1" class="photo-input__file" @change="onPicked" />

    <photo-gallery ref="viewer" :files="items" hide-strip />
  </div>
</template>

<script>
// Pick or take photos, re-encode them in the browser (helper/image.js) and upload each one right away
// (POST /files). v-model holds only the uploaded ones: [{ id, width, height, localUrl }].
import AuthImage from "./AuthImage.vue";
import PhotoGallery from "./PhotoGallery.vue";
import { prepareImage } from "@/helper/image";
import { uploadPhoto, photoErrorMessage } from "./api";
import { primePhoto, dropPhoto } from "./photoCache";

let nextKey = 1;

export default {
  name: "PhotoInput",
  components: { AuthImage, PhotoGallery },
  props: {
    value: { type: Array, default: () => [] },
    purpose: {
      type: String,
      required: true,
      validator: v => ["label", "place", "comment"].includes(v),
    },
    max: { type: Number, default: 6 },
    label: { type: String, default: "Fotografii" },
    hint: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      items: (this.value || []).slice(),
      pending: [], // { key, previewUrl, progress, stage, controller }
      // ids uploaded here (still pending on the server): forgotten from the cache when removed
      uploadedHere: {},
      hasCamera: typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches,
    };
  },
  computed: {
    busy() {
      return this.pending.length > 0;
    },
    replacing() {
      return this.max === 1 && this.items.length > 0;
    },
    canPick() {
      return this.max === 1 || this.items.length + this.pending.length < this.max;
    },
    galleryLabel() {
      if (this.replacing) return "Alege alta";
      return this.hasCamera ? "Alege din galerie" : this.max > 1 ? "Alege fotografii" : "Alege fotografia";
    },
  },
  watch: {
    value(v) {
      this.items = (v || []).slice();
    },
    busy(v) {
      this.$emit("uploading", v);
    },
  },
  beforeDestroy() {
    this.destroyed = true;
    this.pending.forEach(p => {
      p.cancelled = true;
      if (p.controller) p.controller.abort();
      this.dispose(p);
    });
  },
  methods: {
    pick(which) {
      const input = this.$refs[which];
      if (input) input.click();
    },
    async onPicked(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = ""; // the same photo can be picked again
      if (!files.length) return;
      let chosen = files;
      if (this.max === 1) {
        chosen = files.slice(0, 1);
      } else {
        const room = this.max - this.items.length - this.pending.length;
        if (files.length > room) {
          chosen = files.slice(0, Math.max(0, room));
          this.$emit("error", `Se pot adăuga cel mult ${this.max} fotografii.`);
        }
      }
      const jobs = chosen.map(file => ({ file, entry: this.addPending() }));
      // one at a time: a full-size photo needs a lot of memory on a phone
      for (const job of jobs) {
        if (this.destroyed) break;
        if (!this.pending.includes(job.entry)) continue; // cancelled meanwhile
        await this.process(job.file, job.entry);
      }
    },
    addPending() {
      const entry = { key: nextKey++, previewUrl: null, progress: 0, stage: "prepare", controller: null, cancelled: false };
      this.pending.push(entry);
      return entry;
    },
    async process(file, entry) {
      try {
        const photo = await prepareImage(file);
        if (entry.cancelled) return;
        entry.previewUrl = URL.createObjectURL(photo.thumb);
        entry.stage = "upload";
        entry.controller = typeof AbortController === "function" ? new AbortController() : null;
        const saved = await uploadPhoto(photo, this.purpose, {
          signal: entry.controller ? entry.controller.signal : undefined,
          onProgress: p => (entry.progress = p),
        });
        if (entry.cancelled || this.destroyed) return;
        // shown from memory from now on (thumbnail + full photo for the viewer)
        const localUrl = primePhoto(saved.id, "thumb", photo.thumb);
        primePhoto(saved.id, "full", photo.blob);
        this.uploadedHere[saved.id] = true;
        const item = { id: saved.id, width: saved.width || photo.width, height: saved.height || photo.height, localUrl };
        if (this.max === 1) {
          this.items.forEach(old => this.forget(old));
          this.items = [item];
        } else {
          this.items = [...this.items, item];
        }
        this.$emit("input", this.items.slice());
      } catch (error) {
        if (entry.cancelled || this.destroyed) return;
        this.$emit("error", photoErrorMessage(error));
      } finally {
        this.removePending(entry);
      }
    },
    removePending(entry) {
      this.dispose(entry);
      this.pending = this.pending.filter(p => p !== entry);
    },
    dispose(entry) {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      entry.previewUrl = null;
    },
    cancel(entry) {
      entry.cancelled = true;
      if (entry.controller) entry.controller.abort();
      this.removePending(entry);
    },
    forget(item) {
      if (this.uploadedHere[item.id]) {
        dropPhoto(item.id);
        delete this.uploadedHere[item.id];
      }
    },
    remove(item) {
      this.forget(item);
      this.items = this.items.filter(i => i !== item);
      this.$emit("input", this.items.slice());
    },
    openViewer(i) {
      this.$refs.viewer.open(i);
    },
  },
};
</script>

<style scoped>
.photo-input {
  position: relative;
}
.photo-input__head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.photo-input__label {
  font-size: 14px;
  font-weight: 600;
  color: #2d8659;
}
.photo-input__count {
  margin-left: auto;
  font-size: 13px;
  color: #757575;
}
.photo-input__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}
.photo-input__tile {
  position: relative;
  aspect-ratio: 1 / 1;
  min-height: 96px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
}
.photo-input__open {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  cursor: zoom-in;
}
.photo-input__tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.photo-input__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(14, 42, 33, 0.55);
  color: white;
}
.photo-input__percent {
  font-size: 12px;
  font-weight: 600;
}
.photo-input__stage {
  margin-top: 6px;
  font-size: 12px;
}
.photo-input__remove {
  position: absolute !important;
  top: 4px;
  right: 4px;
  width: 32px !important;
  height: 32px !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3) !important;
}
.photo-input__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.photo-input__btn {
  flex: 1 1 180px;
  min-height: 48px !important;
  text-transform: none;
  letter-spacing: normal;
  font-size: 15px;
  border-radius: 8px;
}
.photo-input__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #757575;
}
.photo-input__file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
}
</style>
